/**
 * Implementation of IFloodgateTelemetryLogger for Web SDK
 */

import { Api, TreatmentType } from "@ms-ofb/floodgate-shared-typings";
import * as Logging from "../Logging/Logging";
import { isFloodgate2 } from "../Logging/utils";

export default class IFloodgateTelemetryLogger implements Api.IFloodgateTelemetryLogger {

	/**
	 * Log that a survey's trigger was met.
	 * @param campaignId Id of the campaign the survey belongs to.
	 * @param surveyId Id of the survey.
	 * @param surveyType Type of the survey.
	 * @param surveyActivityInfo The survey's activities' information such as names, counts, isAggregate and if the list is ordered.
	 * @param additionalSurveyInfo additional survey info in a stringified json object:
	 * 		- surveyLauncherType: LauncherType of the survey.
	 * 		- channelType: The channel type of the survey's campaign.
	 * 		- surveyPercentageNumerator: Percentage numerator of the survey's Campaign.
	 * 		- surveyPercentageDenominator: Percentage denominator of the survey's Campaign.
	 * 		- surveyNominationPeriodNumber: The Nomination Period of the survey's Campaign in seconds, -1 if not applicable.
	 * 		- surveyCooldownPeriodNumber: The Cooldown Period of the survey's Campaign in seconds, -1 if not applicable.
	 * 		- allCandidateCampaignDefinitionIDs: The list of campaign definition IDs that have corresponding surveys which are candidates.
	 * 		- allCampaignDefinitionIDs: The list of all campaign definition IDs.
	 * @param treatmentType Type of campaign the user was selected for (ex. Control, TreatmentA, TreatmentB, etc). Default is type none.
	 * @param isUxSchemaPresent Whether UxSchema is present for this session
	 */
	public log_TriggerMet(campaignId: string, surveyId: string, surveyType: Api.SurveyType, surveyActivityInfo: string,
		additionalSurveyInfo: string, treatmentType: TreatmentType, isUxSchemaPresent: boolean): void {
		Logging.getLogger().logEvent(Logging.EventIds.SURVEY_FLOODGATE_TRIGGERMET,
			Logging.LogLevel.Critical,
			{
				CampaignId: campaignId,
				SurveyId: surveyId,
				SurveyType: surveyType,
				SurveyActivityInfo: surveyActivityInfo,
				AdditionalSurveyInfo : additionalSurveyInfo,
				TreatmentType: treatmentType,
				IsFloodgate2: isFloodgate2(isUxSchemaPresent),
			});
	}

	/**
	 * Log that a user was selected(nominated) for a survey if it's not sampled
	 * @param campaignId Id of the campaign the survey belongs to.
	 * @param surveyId Id of the survey.
	 * @param surveyType Type of the survey.
	 * @param additionalSurveyInfo additional survey info in a stringified json object:
	 * 		- surveyLauncherType LauncherType of the survey.
	 * 		- channelType The channel type of the survey's campaign.
	 * 		- surveyPercentageNumerator Percentage numerator of the survey's Campaign.
	 * 		- surveyPercentageDenominator Percentage denominator of the survey's Campaign.
	 * 		- surveyNominationPeriodNumber The Nomination Period of the survey's Campaign in seconds, -1 if not applicable.
	 * 		- surveyCooldownPeriodNumber The Cooldown Period of the survey's Campaign in seconds, -1 if not applicable.
	 * 		- allCandidateCampaignDefinitionIDs: The list of campaign definition IDs that have corresponding surveys which are candidates.
	 * 		- allCampaignDefinitionIDs: The list of all campaign definition IDs.
	 * @param treatmentType Type of campaign the user was selected for (ex. Control, TreatmentA, TreatmentB, etc). Default is type none.
	 */
	public log_UserSelected(campaignId: string, surveyId: string, surveyType: Api.SurveyType, additionalSurveyInfo: string,
		treatmentType: TreatmentType): void {

		Logging.getLogger().logEvent(Logging.EventIds.SURVEY_FLOODGATE_USERSELECTED,
			Logging.LogLevel.Critical,
			{
				CampaignId: campaignId,
				SurveyId: surveyId,
				SurveyType: surveyType,
				AdditionalSurveyInfo : additionalSurveyInfo,
				TreatmentType: treatmentType,
			});
	}

	/**
	 * Log that campaign definitions failed to load.
	 * @param errorMessage error message
	 */
	public log_CampaignLoad_Failed(errorMessage: string): void {
		Logging.getLogger().logEvent(Logging.EventIds.SURVEY_FLOODGATE_CAMPAIGNLOAD_FAILED,
			Logging.LogLevel.Error,
			{
				ErrorMessage: errorMessage,
			});
	}

	/**
	 * Log an event with error message
	 * @param eventId event id
	 * @param errorMessage error message
	 */
	public log_Error(eventId: string, errorMessage: string): void {
		const event: Logging.IEventId = { name: eventId };
		Logging.getLogger().logEvent(event,
			Logging.LogLevel.Error,
			{
				ErrorMessage: errorMessage,
			});
	}

	/**
	 * Log an event with properties
	 * @param eventId event id
	 * @param properties telemetry event properties
	 * @param internalSampleRate sample rate for this event in decimal (example: 0.05 for 5%). (Does not override host set sampleRate)
	 */
	public log_Event(eventId: string, properties: Api.ITelemetryProperties, internalSampleRate?: number): void {
		const event: Logging.IEventId = { name: eventId };
		Logging.getLogger().logEvent(event, Logging.LogLevel.Critical, properties, internalSampleRate);
	}

	/**
	 * Log message locally
	 * @param message message
	 * @param data message data
	 */
	public log_LocalError(message: string, ...data: any[]): void {
		this.log_Local(Logging.LogLevel.Error, message, data);
	}

	private log_Local(logSeverity: Logging.LogLevel, message: string, ...others: any[]): void {
		Logging.getLogger().logLocal(logSeverity, others);
	}
}
